import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { setSession } from '../lib/auth';
import { useT } from '../i18n';
import LangSwitch from '../components/LangSwitch';
import type { Session } from '../types/token';

interface LoginProps {
  onLogin: (session: Session) => void;
}

export default function Login({ onLogin }: LoginProps) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingSession, setPendingSession] = useState<Session | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.passwordStatus()
      .then((status) => {
        if (!cancelled) setSetupMode(!status.configured && status.setup_allowed);
      })
      .catch(() => {
        // Older or temporarily unavailable servers still support legacy access keys.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!password || submitting) return;
    if (setupMode && password !== confirmation) {
      setError(t('login.errorMismatch'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const session = await api.login(password, setupMode);
      setPassword('');
      setConfirmation('');
      setPendingSession(session);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t('login.errorInvalid'));
      } else if (err instanceof ApiError && err.status === 429) {
        setError(t('login.errorRateLimited'));
      } else {
        setError(err instanceof Error ? err.message : t('login.errorGeneric'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleTourMode() {
    setError(null);
    setPendingSession({ kind: 'guest', company: 'Tour' });
  }

  function handleConfirmNotice() {
    if (!pendingSession) return;
    setSession(pendingSession);
    onLogin(pendingSession);
  }

  function handleCancelNotice() {
    setPendingSession(null);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-6 py-10 animate-fade-in">
      <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-6 shadow-card transition-shadow duration-200 hover:shadow-float md:p-8">
        <div className="flex justify-end mb-8">
          <LangSwitch variant="default" />
        </div>

        <div className="mb-8">
          <p className="text-xs font-mono text-coffee mb-2 uppercase tracking-[0.22em]">{t('login.kicker')}</p>
          <h1 className="text-3xl font-semibold leading-tight text-cocoa">
            {t('login.title')}
          </h1>
          <p className="text-sm text-coffee mt-2 leading-relaxed">
            {t(setupMode ? 'login.setupSubtitle' : 'login.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-mono text-coffee/80 uppercase tracking-wider">
              {t('login.passwordLabel')}
            </span>
            <span className="relative">
              <input
                type={passwordVisible ? 'text' : 'password'}
                autoFocus
                autoComplete={setupMode ? 'new-password' : 'current-password'}
                spellCheck={false}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-3 pr-12 font-mono text-sm text-cocoa shadow-sm transition focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-950/10"
              />
              <PasswordVisibilityButton visible={passwordVisible} onToggle={() => setPasswordVisible((current) => !current)} />
            </span>
          </label>
          {setupMode && (
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-mono text-coffee/80 uppercase tracking-wider">
                {t('login.confirmPasswordLabel')}
              </span>
              <span className="relative">
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  autoComplete="new-password"
                  spellCheck={false}
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-3 pr-12 font-mono text-sm text-cocoa shadow-sm transition focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-950/10"
                />
                <PasswordVisibilityButton visible={passwordVisible} onToggle={() => setPasswordVisible((current) => !current)} />
              </span>
            </label>
          )}
          {error && <p className="text-cocoa font-mono text-xs italic">{error}</p>}
          <button
            type="submit"
            disabled={submitting || !password || (setupMode && !confirmation)}
            className="mt-2 rounded-full bg-tan px-4 py-2.5 font-mono text-sm text-neutral-100 shadow-soft transition hover:-translate-y-0.5 hover:bg-neutral-800 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        <div className="mt-8 flex flex-col gap-3 text-xs font-mono text-coffee/70 sm:flex-row sm:items-center sm:justify-between">
          <p>{t('login.hint')}</p>
          <button
            type="button"
            onClick={handleTourMode}
            className="self-start rounded-full border border-neutral-200 px-3 py-1.5 text-cocoa transition hover:-translate-y-0.5 hover:border-tan hover:bg-tan/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15 sm:self-auto"
          >
            {t('login.tourButton')}
          </button>
        </div>
      </div>

      {pendingSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-cocoa/60 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="access-notice-title"
            className="w-full max-w-md rounded-2xl border border-white/60 bg-white p-6 shadow-float"
          >
            <h2 id="access-notice-title" className="text-xl font-semibold text-cocoa">
              {t('login.noticeTitle')}
            </h2>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-coffee">
              <p>{t('login.noticeBody')}</p>
              <p className="rounded-xl border border-tan/30 bg-tan/10 px-3 py-2 font-medium text-cocoa">
                {t('login.noticeConstruction')}
              </p>
              {pendingSession.kind === 'guest' && <p>{t('login.noticeTourBody')}</p>}
            </div>
            <div className="mt-6 flex justify-end gap-3 font-mono text-sm">
              <button
                type="button"
                onClick={handleCancelNotice}
                className="rounded-full px-4 py-2 text-coffee transition hover:bg-neutral-100 hover:text-cocoa focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
              >
                {t('login.noticeCancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmNotice}
                className="rounded-full bg-tan px-4 py-2 text-neutral-100 shadow-soft transition hover:-translate-y-0.5 hover:bg-neutral-800 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
              >
                {t('login.noticeConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PasswordVisibilityButton({ visible, onToggle }: { visible: boolean; onToggle: () => void }) {
  const t = useT();
  const label = t(visible ? 'login.hidePassword' : 'login.showPassword');
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      aria-pressed={visible}
      title={label}
      className="absolute inset-y-0 right-1.5 my-auto flex h-9 w-9 items-center justify-center rounded-lg text-coffee/65 transition hover:bg-neutral-100 hover:text-cocoa focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
    >
      {visible ? (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3l18 18" />
          <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
          <path d="M9.9 4.4A10.7 10.7 0 0 1 12 4c5.4 0 9 5.2 9 5.2a15.6 15.6 0 0 1-2.1 2.7" />
          <path d="M6.6 6.6C4.3 8.2 3 10.2 3 10.2S6.6 16 12 16c1 0 1.9-.2 2.8-.5" />
        </svg>
      ) : (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12s3.6-5.8 9-5.8 9 5.8 9 5.8-3.6 5.8-9 5.8S3 12 3 12Z" />
          <circle cx="12" cy="12" r="2.4" />
        </svg>
      )}
    </button>
  );
}
